// hook

// types
import type { DealHistory } from '@makemydeal/dr-dash-types';
import type { FC } from 'react';

// selectors
import * as accessoriesSelectors from '@makemydeal/dr-dash-store/selectors/accessoriesSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';

// components
import DealSummaryItem from '../common/DealSummaryItem';
import LineItemWithChildren from '../common/LineItemWithChildren';

// constants
import * as constants from '../../constants';

// utils
import { formatDollarsAndCents, formatNumber } from '@makemydeal/dr-common-utils';
import {
    labelStyles as defaultLabelStyles,
    expandibleButtonStyles,
    expandibleAreaStyles,
    valueStyles as defaultValueStyles,
    secondaryValueStyles,
    detailedViewGridProps,
    detailedViewLabelStyles,
    detailedViewSecondaryGridProps,
    detailedViewValueStyles
} from '../utils/styles';
import { isEqual } from 'lodash';

import { useDealSummary } from '../../common/DealSummaryProvider';
import { useMediaQuery } from 'react-responsive';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { Grid } from '@interstate/components/Grid';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';
import { useHeldValue } from '../../hooks/useHeldSelector';

export type AdjustedResidualProps = {
    deal?: DealHistory;
};

const AdjustedResidual: FC<AdjustedResidualProps> = ({ deal }) => {
    const useDealRootSelector = createDealRootSelector(deal, 'AdjustedResidual');
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const { showDetailedView } = useDealSummary();

    const { baseResidualAmount: baseResidual, baseResidualPercent } = useTrackedSelector(
        offerSelectors.getResidualInfoOverrideWithFallback,
        isEqual,
        { component: 'AdjustedResidual', field: 'residualInfoOverrideWithFallback' }
    );
    const baseResidualPercentValue = baseResidualPercent != null ? baseResidualPercent : '--';

    const adjustedResidualAmount = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getAdjustedResidualValue, {
            component: 'AdjustedResidual',
            field: 'adjustedResidualValue'
        })
    );
    const adjustedResidualPercent = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getAdjustedResidualPercent, {
            component: 'AdjustedResidual',
            field: 'adjustedResidualPercent'
        })
    );
    const maxResidualizedMsrpApplied = useTrackedSelector(offerReduxSelectors.getMaxResidualizedMsrpApplied, {
        component: 'AdjustedResidual',
        field: 'maxResidualizedMsrpApplied'
    });
    const MRMORMSRPLABEL = maxResidualizedMsrpApplied ? constants.MAX_RESIDUALIZED_MSRP : constants.MSRP;
    const mrmMsrp = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getMaxResidualizedMsrp, {
            component: 'MRMORMSRPLABEL',
            field: 'maxResidualizedMsrp'
        })
    );
    const msrp = useTrackedSelector(offerSelectors.getRetailPriceOverrideWithFallback, {
        component: 'MRMORMSRPLABEL',
        field: 'retailPriceOverrideWithFallback'
    });
    const maxResidualizedMsrp = maxResidualizedMsrpApplied ? mrmMsrp : msrp;

    const formattedMaxResidualizedMsrp = formatDollarsAndCents(maxResidualizedMsrp);
    const formattedAdjustedResidual = formatDollarsAndCents(adjustedResidualAmount);
    const formattedAdjustedResidualPercent = `${adjustedResidualPercent}%`;
    const formattedBaseResidual = formatDollarsAndCents(baseResidual);
    const formattedBaseResidualPercent = `${baseResidualPercentValue}%`;

    const milesPerYear = useHeldValue(useDealRootSelector(offerReduxSelectors.getAnnualMiles, 'annualMiles'), deal);
    const existingMileage = useDealRootSelector(vehicleSelectors.getMileage, 'mileage');
    const accessories = useTrackedSelector(accessoriesSelectors.getAccessoriesList, {
        component: 'MRMORMSRPLABEL',
        field: 'accessoriesList'
    });
    const accessoriesWithResidualValues = accessories
        .filter((accessory) => accessory.residual > 0)
        .map((accessory) => ({
            name: accessory.name,
            amount: accessory.residual
        }));
    const residualizedAccessoriesValue = accessoriesWithResidualValues.reduce((acc, accessory) => acc + accessory.amount, 0);

    const formattedResidualizedAccessoriesValue = formatDollarsAndCents(residualizedAccessoriesValue);

    let gridProps = {};
    if (showDetailedView && isLargeSize) {
        gridProps = {
            ...detailedViewGridProps,
            pt: '0.571rem',
            rowGap: '0.571rem'
        };
    } else {
        gridProps = detailedViewSecondaryGridProps;
    }
    const labelStyles = showDetailedView ? detailedViewLabelStyles : {};
    const valueStyles = showDetailedView ? secondaryValueStyles : {};

    return (
        <DealSummaryItem
            valueStyles={showDetailedView ? detailedViewValueStyles : defaultValueStyles}
            labelStyles={showDetailedView ? {} : defaultLabelStyles}
            label={constants.TOTAL_ADJUSTED_RESIDUAL}
            value={formattedAdjustedResidual}
        >
            <Grid {...gridProps}>
                <LineItemWithChildren
                    label={MRMORMSRPLABEL}
                    value={formattedMaxResidualizedMsrp}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                <LineItemWithChildren
                    label={constants.BASE_RESIDUAL_PERCENT}
                    value={formattedBaseResidualPercent}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                <LineItemWithChildren
                    label={constants.BASE_RESIDUAL_DOLLAR}
                    value={formattedBaseResidual}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                <LineItemWithChildren
                    label={constants.MILES_PER_YEAR}
                    value={formatNumber(milesPerYear)}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                <LineItemWithChildren
                    label={constants.EXISTING_MILEAGE}
                    value={formatNumber(existingMileage)}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                <LineItemWithChildren
                    label={constants.ADJUSTED_RESIDUAL_PERCENT}
                    value={formattedAdjustedResidualPercent}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                <LineItemWithChildren
                    label={constants.ADJUSTED_RESIDUAL_DOLLAR}
                    value={formattedAdjustedResidual}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                {!!accessoriesWithResidualValues.length && (
                    <DealSummaryItem
                        valueStyles={secondaryValueStyles}
                        labelStyles={labelStyles}
                        label={constants.RESIDUALIZED_ACCESSORIES}
                        value={formattedResidualizedAccessoriesValue}
                        expandibleButtonStyles={expandibleButtonStyles}
                        expandibleAreaStyles={expandibleAreaStyles}
                    >
                        {accessoriesWithResidualValues.map((accessoryResidual) => (
                            <LineItemWithChildren
                                key={accessoryResidual.name}
                                label={accessoryResidual.name}
                                value={formatDollarsAndCents(accessoryResidual.amount)}
                            />
                        ))}
                    </DealSummaryItem>
                )}
            </Grid>
        </DealSummaryItem>
    );
};

export default AdjustedResidual;
